// Ping/pong fixture: the smallest protocol that exercises the engine.
// A pinger sends a ping, a ponger replies, and a retry timer resends the same
// ping identity on timeout. The ponger deduplicates on ping id, mirroring the
// Lab 1 AMO cache in miniature.
//
// config: { pinger, ponger, linkDelay=1, retryTimeout=5 }
import { createEvent, EventKind } from '../../core/types';
import { checkPingPongInvariants } from './invariants';

export const MSG_PING = 'ping';
export const MSG_PONG = 'pong';
export const TIMER_RETRY = 'retry';

export const labMeta = {
  id: 'pingpong',
  title: 'Playground · Ping/pong',
  description: 'The smallest protocol on the engine: one ping, one pong, one retry timer.',
};

export function sampleCommands(config) {
  return [{ label: 'Send ping', to: config.pinger, command: { op: 'sendPing' } }];
}

export function summarizeNode(nodeId, node) {
  const { durable, volatile } = node;
  if (durable.role === 'pinger') {
    return [
      ['role', 'pinger'],
      ['outstanding', volatile.outstanding ? volatile.outstanding.pingId : 'none'],
      ['completed', String(durable.completedCommands.length)],
      ['sent', durable.sentPingIds.join(', ') || 'none'],
    ];
  }
  return [
    ['role', 'ponger'],
    ['executed', String(durable.executedCount)],
    ['seen', volatile.seenPingIds.join(', ') || 'none'],
    ['pongs sent', String(volatile.pongsSent)],
  ];
}

export function defaultConfig(pinger = 'A', ponger = 'B') {
  return { pinger, ponger, linkDelay: 1, retryTimeout: 5 };
}

export function initialize(config) {
  const { pinger, ponger } = config;
  return {
    nodes: {
      [pinger]: {
        durable: {
          role: 'pinger',
          nextPingId: 1,
          sentPingIds: [],
          commandsAccepted: 0,
          completedCommands: [],
          acceptedPongs: [],
        },
        volatile: { outstanding: null },
      },
      [ponger]: {
        durable: { role: 'ponger', executedCount: 0, executedPingIds: [] },
        volatile: { seenPingIds: [], cachedResults: {}, pongsSent: 0 },
      },
    },
    initialEvents: [],
  };
}

export function freshVolatile(durable) {
  if (durable.role === 'pinger') return { outstanding: null };
  return { seenPingIds: [], cachedResults: {}, pongsSent: 0 };
}

function cloneNodePart(part) {
  return JSON.parse(JSON.stringify(part));
}

function sendPing(state, config, nodeId, command) {
  const node = state.nodes[nodeId];
  const durable = cloneNodePart(node.durable);
  const volatile = cloneNodePart(node.volatile);
  if (volatile.outstanding) {
    return {
      durable,
      volatile,
      note: 'pinger busy; command ignored while ' + volatile.outstanding.pingId + ' outstanding',
    };
  }
  const pingId = nodeId + '#' + durable.nextPingId;
  durable.nextPingId += 1;
  durable.sentPingIds.push(pingId);
  durable.commandsAccepted += 1;
  volatile.outstanding = { pingId, command };
  return {
    durable,
    volatile,
    outbox: [
      {
        to: config.ponger,
        payload: { msgType: MSG_PING, pingId, command },
        delay: config.linkDelay,
        label: 'ping ' + pingId,
      },
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_RETRY + ':' + pingId,
        timerType: TIMER_RETRY,
        payload: { pingId },
        delay: config.retryTimeout,
        label: 'retry ' + pingId,
      },
    ],
    note: 'send ping ' + pingId,
  };
}

function onPing(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = cloneNodePart(node.durable);
  const volatile = cloneNodePart(node.volatile);
  const { pingId, command } = event.payload;
  if (volatile.seenPingIds.includes(pingId)) {
    volatile.pongsSent += 1;
    return {
      durable,
      volatile,
      outbox: [
        {
          to: event.from,
          payload: {
            msgType: MSG_PONG,
            pingId,
            result: volatile.cachedResults[pingId],
            cached: true,
          },
          delay: config.linkDelay,
          label: 'cached pong ' + pingId,
        },
      ],
      note: 'duplicate ping ' + pingId + '; resend cached pong',
    };
  }
  volatile.seenPingIds.push(pingId);
  const result = { echo: command };
  volatile.cachedResults[pingId] = result;
  volatile.pongsSent += 1;
  durable.executedCount += 1;
  durable.executedPingIds.push(pingId);
  return {
    durable,
    volatile,
    outbox: [
      {
        to: event.from,
        payload: { msgType: MSG_PONG, pingId, result, cached: false },
        delay: config.linkDelay,
        label: 'pong ' + pingId,
      },
    ],
    note: 'execute ping ' + pingId + ' and reply',
  };
}

function onPong(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = cloneNodePart(node.durable);
  const volatile = cloneNodePart(node.volatile);
  const { pingId } = event.payload;
  if (volatile.outstanding && volatile.outstanding.pingId === pingId) {
    durable.completedCommands.push(volatile.outstanding.command);
    durable.acceptedPongs.push(pingId);
    volatile.outstanding = null;
    return {
      durable,
      volatile,
      timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + pingId }],
      note: 'complete ' + pingId,
    };
  }
  return { durable, volatile, note: 'stale pong ' + pingId + ' ignored' };
}

function onRetry(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = cloneNodePart(node.durable);
  const volatile = cloneNodePart(node.volatile);
  const { pingId } = event.payload;
  if (volatile.outstanding && volatile.outstanding.pingId === pingId) {
    return {
      durable,
      volatile,
      outbox: [
        {
          to: config.ponger,
          payload: {
            msgType: MSG_PING,
            pingId,
            command: volatile.outstanding.command,
          },
          delay: config.linkDelay,
          label: 'ping ' + pingId + ' (retry)',
        },
      ],
      timerOps: [
        {
          action: 'set',
          timerKey: TIMER_RETRY + ':' + pingId,
          timerType: TIMER_RETRY,
          payload: { pingId },
          delay: config.retryTimeout,
          label: 'retry ' + pingId,
        },
      ],
      note: 'timeout; resend ' + pingId + ' with same identity',
    };
  }
  return { durable, volatile, note: 'stale retry timer discarded' };
}

export function handleClientCommand(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node || node.durable.role !== 'pinger') {
    return { note: 'command sent to non-pinger; ignored' };
  }
  return sendPing(state, config, nodeId, event.payload.command);
}

export function handleMessage(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'message to unknown node; ignored' };
  if (event.payload.msgType === MSG_PING && node.durable.role === 'ponger') {
    return onPing(state, config, nodeId, event);
  }
  if (event.payload.msgType === MSG_PONG && node.durable.role === 'pinger') {
    return onPong(state, config, nodeId, event);
  }
  return { note: 'unexpected ' + event.payload.msgType + ' at ' + nodeId + '; ignored' };
}

export function handleTimer(state, nodeId, event) {
  const config = state.config;
  if (event.payload.timerType === TIMER_RETRY) {
    return onRetry(state, config, nodeId, event);
  }
  return { note: 'unknown timer; ignored' };
}

export function checkInvariants(state) {
  return checkPingPongInvariants(state);
}

export function describeEvent(event) {
  const p = event.payload || {};
  if (event.kind === EventKind.MESSAGE) {
    if (p.msgType === MSG_PING) return 'deliver ping ' + p.pingId;
    if (p.msgType === MSG_PONG) {
      return 'deliver pong ' + p.pingId + (p.cached ? ' (cached)' : '');
    }
    return 'deliver message';
  }
  if (event.kind === EventKind.TIMER) return 'fire timer ' + (p.timerKey || '');
  if (event.kind === EventKind.CLIENT_COMMAND) return 'submit command';
  return event.label || event.kind;
}

// Adapter object consumed by the engine.
const pingPongAdapter = {
  initialize,
  handleMessage,
  handleClientCommand,
  handleTimer,
  freshVolatile,
  checkInvariants,
  describeEvent,
  summarizeNode,
};

export default pingPongAdapter;
export { createEvent };
